import {
    type ApplicationSettingThemeColor,
    type AutoLayout,
    type BackgroundProtocol,
    type VariableADTvalue,
    VariableType
} from '@lighthouse/core'
import { safeToString } from '@lighthouse/tools'

import { SEND_EMAIL_WIDTH } from '../../../constants/flow'
import type { EmailParticipant, SendEmailActionConfig, SendEmailBody } from '../../../types'
import { EMAIL_EDIT_TYPE, PARAGRAPH_WIDTH_TYPE } from '../../../types'
import { getBackgroundStyle, getBorderStyle, getPaddingStyle } from '../../design'
import { isTextValue } from '../../helper'
import { getLayoutStyle } from '../../layout'
import type { ExtraParams } from './common'
import { getArgsResult, getRecordResult, modifyVariableValue } from './common'

const generatorEmailStyle = (
    backgroundConfig: BackgroundProtocol,
    paddingConfig: AutoLayout['padding'],
    gap: AutoLayout['gap'],
    palettes: ApplicationSettingThemeColor[]
) => {
    const background = getBackgroundStyle({ value: backgroundConfig, palettes })
    const padding = getPaddingStyle(paddingConfig)
    const flex = getLayoutStyle({ align: { direction: 'vertical', alignX: 'center' }, gap })
    const style: React.CSSProperties = {
        ...background,
        ...padding,
        ...flex
    }
    return style
}

const generatorEmailBodyStyle = (body: SendEmailBody, palettes: ApplicationSettingThemeColor[]) => {
    const {
        background: backgroundConfig,
        padding: paddingConfig,
        width: widthSize = PARAGRAPH_WIDTH_TYPE.NORMAL,
        border: borderConfig
    } = body
    const background = getBackgroundStyle({ value: backgroundConfig, palettes })
    const padding = getPaddingStyle(paddingConfig)
    const width: React.CSSProperties = widthSize === PARAGRAPH_WIDTH_TYPE.FULL ? {} : { maxWidth: `${SEND_EMAIL_WIDTH[widthSize]}px` }
    const border = getBorderStyle(borderConfig, palettes)
    const size: React.CSSProperties = { width: '100%', boxSizing: 'border-box' }
    const style: React.CSSProperties = {
        ...size,
        ...background,
        ...padding,
        ...width,
        ...border
        // ...flex,
    }
    return style
}

function toKebabCase(key: string): string {
    return key.replace(/([A-Z])/g, '-$1').toLowerCase()
}

const generatorHtml = (styles: React.CSSProperties, content?: string) => {
    // 拼接 style 属性
    const divElement = document.createElement('div')
    Object.entries(styles).forEach(([key, value]) => {
        if (typeof value === 'string') {
            const transformKey = toKebabCase(key)
            divElement.style.setProperty(transformKey, value)
        }
    })
    divElement.innerHTML = content || ''
    // 生成最终的 HTML 字符串
    return divElement.outerHTML
}

// 正则表达式匹配 <variable> 标签及其内容
const outputStr = (inputStr: string, excParams: ExtraParams) => {
    return inputStr.replace(/<variable\s+data-value="([^"]*)"><\/variable>/g, (_, dataValue) => {
        // 替换 HTML 实体
        const jsonString = dataValue.replaceAll('&quot;', '"')
        // 解析 JSON 并转换为字符串
        const data = JSON.parse(jsonString) as VariableADTvalue
        const d = modifyVariableValue({
            node: data,
            extraParams: excParams,
            options: {
                useFileUrl: true,
                format: true
            }
        })
        if (!d) {
            return ''
        }

        return String(d)
    })
}

type GenerateHtmlBySendEmailPayload = {
    config: SendEmailActionConfig
    excParams: ExtraParams
}

export const generateHtmlBySendEmail = (payload: GenerateHtmlBySendEmailPayload) => {
    const { config, excParams } = payload
    const { palettes } = excParams
    const { body, background, padding } = config
    const { paragraph, editType, gap } = body
    const emailBodyStyle = generatorEmailBodyStyle(body, palettes || [])

    const bodyInnerHtml = paragraph.reduce((prev, cur) => {
        if (editType === EMAIL_EDIT_TYPE.HTML) {
            // const resultBodyHtml = generatorHtml(emailBodyStyle, cur.targetHtml || '')
            const resultParagraphHtml = cur.targetHtml || ''
            prev += resultParagraphHtml
            return prev
        }
        const resultParagraphHtml = cur.targetRichtext || ''
        prev += resultParagraphHtml
        return prev
    }, '')
    const result = outputStr(bodyInnerHtml, excParams)
    const bodyHtml = generatorHtml(emailBodyStyle, result)
    const emailStyle = generatorEmailStyle(background, padding, gap, palettes || [])
    const html = generatorHtml(emailStyle, bodyHtml)

    return html.replace(/>(.*?)</g, match => {
        // 在匹配的内容中移除所有&nbsp;
        return match.replace(/&nbsp;+/g, '')
    })
}

export const getEmails = (emailParticipants: EmailParticipant[], extraParams?: ExtraParams): string[] => {
    return emailParticipants.map(item => {
        switch (item.type) {
            case VariableType.VALUE: {
                return safeToString(item.valueVariable?.value) ?? ''
            }
            case VariableType.UPSTREAM: {
                const { upstreamVariable } = item
                if (!upstreamVariable) {
                    return ''
                }
                const { nodeId, fieldId } = upstreamVariable
                if (!nodeId || !fieldId) {
                    return ''
                }
                const record = getRecordResult(extraParams?.upstreamNodesResult?.[nodeId])
                if (!record || !record.content[fieldId]) {
                    return ''
                }
                const cellValue = record.content[fieldId]?.value
                if (!isTextValue(cellValue)) {
                    return ''
                }
                return cellValue
            }
            case 'ARG_VARIABLE': {
                const { argVariable } = item
                if (!argVariable) {
                    return ''
                }

                const { nodeId, argId } = argVariable
                if (!nodeId || !argId) {
                    return ''
                }

                const args = getArgsResult(extraParams?.upstreamNodesResult?.[nodeId])

                const arg = args?.find(a => a.id === argId)
                return (arg?.value as string) || ''
            }
            default: {
                return ''
            }
        }
    })
}
